import React from 'react';
import { Box, TextField, Typography, RadioGroup, FormControlLabel, Radio, Paper } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

const sectionPaperSx = {
  mb: 3, p: 2,
  border: '2px solid',
  borderColor: 'divider',
  borderRadius: 2,
  background: 'background.default',
} as const;

const sectionTitleSx = {
  fontWeight: 700, mb: 1.5,
  color: (theme: any) => theme.palette.primary.main,
} as const;

const tooltipSx = (theme: any) => ({
  bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
  color: theme.palette.mode === 'dark' ? '#222' : '#fff',
  fontSize: 15,
  borderRadius: 2,
  boxShadow: 3,
  px: 2,
  py: 1,
  fontWeight: 400,
  minWidth: 320,
});

interface CDBConversionSettingsProps {
  sid: string;
  targetCdbSid: string;
  setTargetCdbSid: (v: string) => void;
  newPdbName: string;
  setNewPdbName: (v: string) => void;
  fileNameConvertOption: string;
  setFileNameConvertOption: (v: string) => void;
  sourcePath: string;
  setSourcePath: (v: string) => void;
  targetPath: string;
  setTargetPath: (v: string) => void;
  platform: string;
}

const CDBConversionSettings: React.FC<CDBConversionSettingsProps> = ({
  sid,
  targetCdbSid,
  setTargetCdbSid,
  newPdbName,
  setNewPdbName,
  fileNameConvertOption,
  setFileNameConvertOption,
  sourcePath,
  setSourcePath,
  targetPath,
  setTargetPath,
  platform,
}) => {
  const [sourcePathTouched, setSourcePathTouched] = React.useState(false);
  const [targetPathTouched, setTargetPathTouched] = React.useState(false);

  React.useEffect(() => {
    setSourcePathTouched(false);
    setTargetPathTouched(false);
  }, [platform, sid, targetCdbSid, newPdbName]);

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>

      {/* Target CDB / New PDB */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Target CDB / New PDB</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target CDB SID:</Typography>
          <TextField
            fullWidth
            size="small"
            value={targetCdbSid}
            onChange={e => setTargetCdbSid(e.target.value)}
            placeholder="cdb19"
          />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>New PDB Name:</Typography>
          <TextField
            fullWidth
            size="small"
            value={newPdbName}
            onChange={e => setNewPdbName(e.target.value)}
            placeholder="pdb19"
          />
        </Box>
      </Paper>

      {/* File Name Convert / Copy Option */}
      <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 0 }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>File Name Convert / Copy Option</Typography>
        <RadioGroup
          value={fileNameConvertOption}
          onChange={e => setFileNameConvertOption(e.target.value)}
          sx={{
            '.MuiFormControlLabel-root': { mb: 1.5, alignItems: 'center', display: 'flex' },
            '.MuiSvgIcon-root': { fontSize: 20, ml: 0.5, color: 'primary.main' },
          }}
        >
          <FormControlLabel
            value="convert"
            control={<Radio />}
            label={
              <Tooltip
                title="file_name_convert will be used directly in AutoUpgrade config file. You cannot use this option if you have OMF files."
                placement="right"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <Typography sx={{ fontWeight: 600 }}>Use Source/Target Paths</Typography>
              </Tooltip>
            }
          />
          <FormControlLabel
            value="none"
            control={<Radio />}
            label={
              <Tooltip
                title="Required to set DB_CREATE_FILE_DEST or PDB_FILE_NAME_CONVERT on Target CDB"
                placement="right"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <Typography sx={{ fontWeight: 600 }}>NONE</Typography>
              </Tooltip>
            }
          />
          <FormControlLabel
            value="nocopy"
            control={<Radio />}
            label={
              <Tooltip
                title="It will keep the existing structure for the database files"
                placement="right"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <Typography sx={{ fontWeight: 600 }}>NOCOPY</Typography>
              </Tooltip>
            }
          />
        </RadioGroup>

        {fileNameConvertOption === 'convert' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Path:</Typography>
              <TextField
                fullWidth
                size="small"
                value={sourcePath}
                onChange={e => { setSourcePathTouched(true); setSourcePath(e.target.value); }}
                onFocus={() => setSourcePathTouched(true)}
                placeholder={sid || 'DB1'}
                sx={{ mt: 1, mb: 2 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !sourcePathTouched && sourcePath === '' ? '#8a8a8a' : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Path:</Typography>
              <TextField
                fullWidth
                size="small"
                value={targetPath}
                onChange={e => { setTargetPathTouched(true); setTargetPath(e.target.value); }}
                onFocus={() => setTargetPathTouched(true)}
                placeholder={
                  platform === 'WINDOWS.X64'
                    ? `${targetCdbSid || 'CDB19'}\\${newPdbName || 'PDB19'}`
                    : `${targetCdbSid || 'CDB19'}/${newPdbName || 'PDB19'}`
                }
                sx={{ mt: 1 }}
                InputProps={{
                  sx: {
                    '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color: !targetPathTouched && targetPath === '' ? '#8a8a8a' : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                    background: 'transparent',
                  },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Paper>
  );
};

export default CDBConversionSettings;
